/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  LISTING_BATCH_LABEL,
  REGISTRATION_BATCH,
  SAM_BUILDING_BATCH_LABEL,
  SAM_LISTING_BATCH_LABEL
} from '@/configs/strings/items-labels'
import {
  getBatchNames,
  getDefaultCardDetailsData
} from '@/utils/cards.utils'
import {
  getFileTypeIconUrl,
  setPreviewUrl
} from '@/utils/items'
import { samsUrls } from '@/configs/urls'
import { createId, normalizeValueArrayToString } from '@/utils/general'
import { formatEmptyValue } from '@/utils/assets'

const getCommonValues = (item) => {
  const assetId = item.assetId
  const creators = formatEmptyValue(
    normalizeValueArrayToString(item.creators)
  )
  const fileType = normalizeValueArrayToString(item.fileType)
  const publishers = formatEmptyValue(
    normalizeValueArrayToString(item.publishers)
  )
  const assetBatch = formatEmptyValue(item.assetBatch?.name)
  const assetListingBatch = formatEmptyValue(
    item.buildLicenseListingTerms?.assetListingBatch?.name
  )
  const samsBuildBatch = formatEmptyValue(item.samsBuildBatch?.name)
  const samsListingBatch = getBatchNames({
    batchKey: 'samsListingBatch',
    batchNameKey: 'name',
    containerValue: item.aiUsageLicensingPreferences
  })
  const url = `/${samsUrls.base}/${item.id}`
  const title = item.title
  const isListed = item.listed

  return {
    assetBatch,
    assetId,
    assetListingBatch,
    creators,
    fileType,
    isListed,
    publishers,
    samsBuildBatch,
    samsListingBatch,
    title,
    url
  }
}

export const getSamCardProps = (item) => {
  const {
    assetBatch,
    assetId,
    assetListingBatch,
    creators,
    fileType,
    isListed,
    publishers,
    samsBuildBatch,
    samsListingBatch,
    title,
    url
  } = getCommonValues(item)

  const details = [
    ...getDefaultCardDetailsData({
      assetIdValue: assetId,
      fileTypeValue: fileType,
      publisherValue: publishers
    }),
    {
      label: REGISTRATION_BATCH,
      value: assetBatch
    },
    {
      label: LISTING_BATCH_LABEL,
      value: assetListingBatch
    },
    {
      label: SAM_BUILDING_BATCH_LABEL,
      value: samsBuildBatch
    },
    {
      label: SAM_LISTING_BATCH_LABEL,
      value: samsListingBatch
    }
  ]

  return {
    id: item.id,
    assetId,
    creators,
    details,
    imageUrl: setPreviewUrl(item.coverUrl, fileType),
    isLink: true,
    isListed,
    key: createId(),
    title,
    url
  }
}

export const getItemsPropsForTable = (item) => {
  const {
    assetBatch,
    assetId,
    assetListingBatch,
    creators,
    fileType,
    isListed,
    publishers,
    samsBuildBatch,
    samsListingBatch,
    title,
    url
  } = getCommonValues(item)

  return {
    assetBatch,
    assetId,
    assetListingBatch,
    buttonLabel: 'Manage',
    creators,
    fileType,
    iconUrl: getFileTypeIconUrl(fileType),
    listed: isListed,
    publishers,
    samsBuildBatch,
    samsListingBatch,
    title,
    url
  }
}
export const tableColumns = [
  {
    accessorKey: 'icon',
    enableHiding: false,
    header: ''
  },
  {
    accessorKey: 'assetId',
    enableHiding: false,
    header: 'Asset Id'
  },
  {
    accessorKey: 'title',
    enableHiding: false,
    header: 'Title'
  },
  {
    accessorKey: 'listed',
    enableHiding: false,
    header: 'Listed'
  },
  {
    accessorKey: 'creators',
    header: 'Creators'
  },
  {
    accessorKey: 'publishers',
    header: 'Publishers'
  },
  {
    accessorKey: 'fileType',
    header: 'File Type'
  },
  {
    accessorKey: 'assetBatch',
    header: REGISTRATION_BATCH
  },
  {
    accessorKey: 'assetListingBatch',
    header: LISTING_BATCH_LABEL
  },
  {
    accessorKey: 'samsBuildBatch',
    header: SAM_BUILDING_BATCH_LABEL
  },
  {
    accessorKey: 'samsListingBatch',
    header: SAM_LISTING_BATCH_LABEL
  },
  {
    accessorKey: 'actions',
    enableHiding: false,
    header: ''
  }
]

const defaultHiddenColumns = [
  'creators',
  'publishers',
  'fileType',
  'assetBatch',
  'assetListingBatch',
  'samsBuildBatch',
  'samsListingBatch'
]

export const tableInitialState = {
  columnVisibility: Object.fromEntries(
    defaultHiddenColumns.map((id) => [id, false])
  )
}
